import { ElementRef, Injectable } from '@angular/core';
import {
    IIqGraphLtStateService,
    IIqLoadTruckResponse,
    IIqMcLtGraphParams,
    graphProps,
    IIqLoadsResponse,
    IIqLoadsResponseSchema,
    IIqLoadTruckData,
    IIqTrucksResponse,
    emptyIqLoadTruckData,
    IIqTrucksResponseSchema,
} from '../../models';
import { DatiqGraphqlApolloService } from '@dat/datiq-graphql';
import { firstValueFrom } from 'rxjs';
import { gql } from 'apollo-angular';
import { renderGraph } from './iq-graph-lt-utils';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root',
})
export class IqGraphLtStateService implements IIqGraphLtStateService {
    constructor(private readonly _apollo: DatiqGraphqlApolloService) {}

    async renderLoadsGraph(
        container: ElementRef<any>,
        loadsData: IIqLoadTruckData,
    ): Promise<void> {
        const loadsResponse: IIqLoadTruckData = loadsData;
        if (loadsResponse.errors) {
            return;
        }
        renderGraph(container, loadsResponse.data, this._loadProps);
    }

    async renderTrucksGraph(
        container: ElementRef<any>,
        trucksData: IIqLoadTruckData,
    ): Promise<void> {
        const trucksResponse: IIqLoadTruckData = await trucksData;
        if (trucksResponse.errors) {
            return;
        }
        renderGraph(container, trucksResponse.data, this._graphProps);
    }

    private _loadProps: graphProps = {
        strokeColor: '#5000C5',
    };

    private _graphProps: graphProps = {
        strokeColor: '#0097AF',
    };

    async getLoadsData(params: IIqMcLtGraphParams): Promise<IIqLoadTruckData> {
        let responseLoads: IIqLoadsResponse = await firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    query ($query: MCI_MCIQuery) {
                        loadTruck(query: $query) {
                            loads
                            date
                            geoCode
                        }
                    }
                `,
                variables: {
                    query: {
                        direction: params.direction,
                        equipmentType: params.equipmentType,
                        geoType: params.geoType,
                        geoCode: params.geoCode,
                        timeType: '30_DAYS_BACK',
                    },
                },
            }),
        );

        const parseLoadsResponse =
            IIqLoadsResponseSchema.safeParse(responseLoads);
        if (!parseLoadsResponse.success) {
            return {
                ...emptyIqLoadTruckData,
                loaded: !responseLoads.loading,
                errors: true,
            };
        }

        const loads: IIqLoadTruckResponse[] = this._mapLoadResponse(
            responseLoads.data.loadTruck,
        );

        return { data: loads, loaded: !responseLoads.loading, errors: false };
    }

    async getTruckData(params: IIqMcLtGraphParams): Promise<IIqLoadTruckData> {
        let responseTrucks: IIqTrucksResponse = await firstValueFrom(
            this._apollo.getApollo().query({
                query: gql`
                    query ($query: MCI_MCIQuery) {
                        loadTruck(query: $query) {
                            trucks
                            date
                            geoCode
                        }
                    }
                `,
                variables: {
                    query: {
                        direction: params.direction,
                        equipmentType: params.equipmentType,
                        geoType: params.geoType,
                        geoCode: params.geoCode,
                        timeType: '30_DAYS_BACK',
                    },
                },
            }),
        );

        const parseTrucksResponse =
            IIqTrucksResponseSchema.safeParse(responseTrucks);
        if (!parseTrucksResponse.success) {
            return {
                ...emptyIqLoadTruckData,
                loaded: !responseTrucks.loading,
                errors: true,
            };
        }

        const trucks: IIqLoadTruckResponse[] = this._mapTruckResponse(
            responseTrucks.data.loadTruck,
        );

        return { data: trucks, loaded: !responseTrucks.loading, errors: false };
    }

    private _mapLoadResponse(
        loadsResponse: { geoCode?: string; loads?: number; date?: string }[],
    ): IIqLoadTruckResponse[] {
        return loadsResponse.map((load) => {
            return {
                data: load?.loads,
                date: this._dateParser(load?.date),
            };
        });
    }

    private _mapTruckResponse(
        trucksResponse: { geoCode?: string; trucks?: number; date?: string }[],
    ): IIqLoadTruckResponse[] {
        return trucksResponse.map((truck) => {
            return {
                data: truck?.trucks,
                date: this._dateParser(truck?.date),
            };
        });
    }

    private _dateParser(dateParam: string): string {
        return DateTime.fromFormat(dateParam, 'yyyy-MM-dd').toISO();
    }
}
